import React, { useState, useMemo, useCallback } from "react";
import { Icon, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import ChatbotTest, { CHATBOT_TEST_STATUSES } from "./sections/ChatbotTest";
import Workflow from "./sections/Workflow";
import Warning from "./Warning";
import {
  addOption,
  removeBranch,
  removeOption,
  editMessage,
  editAnswer,
  getTags,
  multipleActions
} from "./selectors";

import styled from "styled-components/macro";

const ChatbotWrapper = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background-color: white;
  border-radius: 4px;
  z-index: 11;
  height: 500px;
  ${({ isFullscreen }) =>
    isFullscreen &&
    `
      position: fixed;
      left: 0;
      top: 0;
      width: 100vw;
      height: 100vh;
      border-radius: 0;
      z-index: 101;
    `};
`;

const WorkflowWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const PageContainer = styled.div`
  overflow: auto;
  flex-grow: 1;
  height: ${({ isFullscreen }) => (isFullscreen ? "600px" : "420px")};
`;

const Warnings = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  left: 8px;
  bottom: 8px;
  z-index: 2;
`;

const ChatbotMap = styled.section`
  display: flex;
  zoom: ${props => props.zoom};
  transition: 0.3s ease;
  > div {
    > div:nth-child(2) {
      border: none;
    }
  }
  scroll-behavior: smooth;
  ::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #00000017;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: #00000052;
  }
`;

const ActionsContainer = styled.div`
  position: absolute;
  bottom: 12px;
  right: 24px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  z-index: 1;
`;

const FullscreenButton = styled.button`
  position: absolute;
  cursor: pointer;
  border-radius: 2px;
  width: 40px;
  height: 40px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  top: 0;
  right: 0;
  margin: 10px;
  border: none;
  padding: 0;
  font-size: 20px;
  z-index: 1;
`;

const ActionButton = styled.button`
  border-radius: 50%;
  background: ${({ theme }) => theme["@grey-lighter"]};
  border: none;
  transition: 0.3s ease;
  cursor: pointer;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme["@grey-darker"]};
  margin-bottom: 10px;
  opacity: ${props => props.disabled && 0.5};
  filter: ${props => props.disabled && "grayscale(1)"};

  &:focus,
  &:active {
    outline: none;
  }

  &:focus,
  &:hover {
    color: black;
  }

  &.active {
    background: ${({ theme }) => theme["@primary"]};
    color: white;
  }
`;

function Chatbot({
  value,
  onChange,
  error,
  warning,
  validateTree,
  disableChat,
  id,
  onSave,
  loading,
  testEnabled = true,
  testProps = {}
}) {
  const { t } = useTranslation(["global", "opportunity"]);
  const [zoom, setZoom] = useState(10);
  const [testStatus, setTestStatus] = useState(CHATBOT_TEST_STATUSES.LOADING);
  const [showTestSidebar, setShowTestSidebar] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tags = useMemo(() => getTags(value), [value]);

  React.useEffect(() => {
    validateTree();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function zoomIn() {
    setZoom(zoom => (zoom < 20 ? zoom + 1 : zoom));
  }

  function zoomOut() {
    setZoom(zoom => (zoom > 6 ? zoom - 1 : zoom));
  }

  const handleChange = useCallback(
    (...props) => {
      setTestStatus(CHATBOT_TEST_STATUSES.SAVE_CHANGES);
      onChange(...props);
    },
    [onChange]
  );

  const handleAddOption = useCallback(
    (id, values) => {
      handleChange(addOption(id, values)(value));
    },
    [handleChange, value]
  );

  const handleRemoveBranch = useCallback(
    id => {
      handleChange(removeBranch(id)(value));
    },
    [handleChange, value]
  );

  const handleRemoveOption = useCallback(
    id => {
      handleChange(removeOption(id)(value));
    },
    [handleChange, value]
  );

  const handleEditMessage = useCallback(
    (id, message) => {
      handleChange(editMessage(id, message)(value));
    },
    [handleChange, value]
  );

  const handleEditAnswer = useCallback(
    (optionId, answerId, answer, tag) => {
      handleChange(editAnswer(optionId, answerId, answer, tag)(value));
    },
    [handleChange, value]
  );

  const handleEditMultiple = useCallback(
    (id, question) => {
      handleChange(multipleActions(id, question)(value));
    },
    [handleChange, value]
  );

  const handleKeyPress = useCallback(e => {
    if (e.key === "Escape" || e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
      setIsFullscreen(false);
    }
  }, []);

  return (
    <ChatbotWrapper
      data-test="chatbot"
      isFullscreen={isFullscreen}
      onKeyDown={handleKeyPress}
      className="chatbot-wrapper"
    >
      <WorkflowWrapper>
        <PageContainer isFullscreen={isFullscreen}>
          <ChatbotMap zoom={zoom / 10}>
            <Workflow
              data={value}
              tags={tags}
              onEditMultiple={handleEditMultiple}
              onAddOption={handleAddOption}
              onRemoveBranch={handleRemoveBranch}
              onRemoveOption={handleRemoveOption}
              onEditMessage={handleEditMessage}
              onEditAnswer={handleEditAnswer}
            />
          </ChatbotMap>
        </PageContainer>
        <Warnings>
          {warning && <Warning severity="warning">{warning}</Warning>}
          {error && (
            <Warning severity="alert" data-test="treeError">
              {error}
            </Warning>
          )}
        </Warnings>
        <Tooltip
          title={t(
            `opportunity:process.stepTypes.chatbot.${
              isFullscreen ? "closeFullscreen" : "openFullscreen"
            }`
          )}
          placement="left"
        >
          <FullscreenButton
            type="button"
            onClick={() => {
              setIsFullscreen(!isFullscreen);
            }}
          >
            <Icon type={isFullscreen ? "fullscreen-exit" : "fullscreen"} />
          </FullscreenButton>
        </Tooltip>
        <ActionsContainer>
          {!disableChat && (
            <Tooltip
              title={t(
                `opportunity:process.stepTypes.chatbot.test.${
                  showTestSidebar ? "close" : "open"
                }`
              )}
              placement="left"
            >
              <ActionButton
                type="button"
                className={showTestSidebar ? "active" : ""}
                onClick={() => {
                  setShowTestSidebar(!showTestSidebar);
                }}
              >
                <Icon type="message" />
              </ActionButton>
            </Tooltip>
          )}
          <ActionButton type="button" disabled={zoom === 20} onClick={zoomIn}>
            <Icon type="zoom-in" />
          </ActionButton>
          <ActionButton type="button" disabled={zoom === 6} onClick={zoomOut}>
            <Icon type="zoom-out" />
          </ActionButton>
        </ActionsContainer>
      </WorkflowWrapper>
      {testEnabled && (
        <ChatbotTest
          {...testProps}
          showTestSidebar={showTestSidebar}
          testStatus={error ? CHATBOT_TEST_STATUSES.INVALID_TREE : testStatus}
          setTestStatus={setTestStatus}
          id={id}
          onSave={onSave}
          loading={loading}
        />
      )}
    </ChatbotWrapper>
  );
}

export default Chatbot;
